import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, X } from 'lucide-react';
import { headerMessageKey, useHeaderMessages } from '@/hooks/useHeaderMessages';
import MessageModal from '@/components/MessageModal';

const HeaderMessage: React.FC = () => {
  const { activeMessages: activeMessagesFromQuery, refetch } = useHeaderMessages();
  const [dismissedMessages, setDismissedMessages] = useState<Set<string>>(new Set());
  const textRef = useRef<HTMLDivElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleChanged = () => refetch();
    window.addEventListener('messages:changed', handleChanged);
    return () => window.removeEventListener('messages:changed', handleChanged);
  }, [refetch]);

  const activeMessages = activeMessagesFromQuery.filter(
    (message) => !message.isDismissible || !dismissedMessages.has(headerMessageKey(message))
  );
  const displayedMessage = activeMessages[0];
  const displayedMessageId = displayedMessage ? headerMessageKey(displayedMessage) : undefined;
  const displayedMessageText = displayedMessage?.messageText;

  useEffect(() => {
    if (activeMessages.length === 0) return;

    const calculateScroll = () => {
      if (textRef.current) {
        const textElement = textRef.current;
        const containerWidth = textElement.parentElement?.clientWidth || 0;
        const textWidth = textElement.scrollWidth;
        const needsScroll = textWidth > containerWidth;

        setScrollDistance(needsScroll ? textWidth - containerWidth + 20 : 0);
      }
    };
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(calculateScroll);
    }, 0);

    window.addEventListener('resize', calculateScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateScroll);
    };
  }, [activeMessages.length, displayedMessageId, displayedMessageText]);

  const handleDismiss = (message: typeof activeMessages[0]) => {
    if (!message.isDismissible) return;
    setDismissedMessages((prev) => new Set([...prev, headerMessageKey(message)]));
  };

  const handleMessageClick = () => {
    setIsModalOpen(true);
  };

  if (activeMessages.length === 0) {
    return null;
  }

  const message = activeMessages[0];
  const isUrgent = message.isUrgent;

  return (
    <>
      <div
        className={`flex items-center pl-3 pr-1 py-1 rounded-md cursor-pointer hover:opacity-80 transition-opacity w-full sm:w-96 sm:flex-1 sm:max-w-md sm:mx-4 ${
          isUrgent
            ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800'
            : 'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800'
        }`}
        onClick={handleMessageClick}
      >
        <AlertCircle className={`h-4 w-4 flex-shrink-0 mr-2 ${
          isUrgent
            ? 'text-red-600 dark:text-red-400'
            : 'text-yellow-600 dark:text-yellow-400'
        }`} />
        <div className="flex-1 overflow-hidden relative min-w-0 mr-2">
          <div
            ref={textRef}
            className={`text-sm whitespace-nowrap ${
              isUrgent
                ? 'text-red-900 dark:text-red-100'
                : 'text-yellow-900 dark:text-yellow-100'
            }`}
            style={scrollDistance > 0 ? {
              animation: 'marquee-dynamic 12s linear infinite',
              animationDelay: '2s',
              '--scroll-distance': `-${scrollDistance}px`
            } as CSSProperties : {}}
          >
            {message.messageText}
          </div>
        </div>
        {message.isDismissible && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss(message);
            }}
            className="h-6 w-6 p-0 hover:bg-destructive/10 flex-shrink-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <MessageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        messages={activeMessages}
      />
    </>
  );
};

export default HeaderMessage;
